import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ExternalLink,
  Loader2,
  MessageSquare,
  Save,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import PersonAvatar from '@/components/common/PersonAvatar';
import ReportThread from '@/components/global-admin/ReportThread';
import { authenticatedBitswanBackendInstance, getAocHost } from '@/lib/api-client';
import { useSupportChat } from '@/context/SupportChatProvider';

type AdvisorReport = {
  id: string;
  advisor_profile_id: string | null;
  advisor_name: string;
  advisor_organization_id: string | null;
  advisor_photo_url: string;
  reporter_avatar_url: string;
  message_count: number;
  last_message_at: string | null;
  unread: number;
  reporter_organization_id: string;
  reporter_organization_name: string;
  reporter_email: string;
  category: string;
  category_display: string;
  details: string;
  status: string;
  status_display: string;
  is_urgent: boolean;
  admin_notes: string;
  handled_by_email: string;
  handled_at: string | null;
  created_at: string;
};

type Board = {
  reports: AdvisorReport[];
  counts: { open: number; investigating: number; urgent_open: number };
};

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'investigating', label: 'Being looked into' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' },
];

const FILTERS = [{ value: '', label: 'Everything' }, ...STATUSES];

const STATUS_STYLE: Record<string, string> = {
  open: 'bg-red-100 text-red-800 hover:bg-red-100',
  investigating: 'bg-amber-100 text-amber-800 hover:bg-amber-100',
  resolved: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
  dismissed: 'bg-slate-100 text-slate-700 hover:bg-slate-100',
};

function when(value: string | null) {
  return value ? new Date(value).toLocaleString() : '—';
}

function advisorProfileUrl(profileId: string) {
  return `${window.location.protocol}//${getAocHost(window.location.hostname)}/public/advisor/${profileId}`;
}

function ReportCard({ report }: { report: AdvisorReport }) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = React.useState(report.admin_notes);
  const [notesTouched, setNotesTouched] = React.useState(false);
  const [talking, setTalking] = React.useState(report.unread > 0);

  React.useEffect(() => {
    if (!notesTouched) setNotes(report.admin_notes);
  }, [report.admin_notes, notesTouched]);

  const update = useMutation({
    mutationFn: async (patch: { status?: string; admin_notes?: string }) => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.patch(`/global-admin/advisor-reports/${report.id}/`, patch);
      return res.data as AdvisorReport;
    },
    onSuccess: () => {
      setNotesTouched(false);
      queryClient.invalidateQueries({ queryKey: ['advisor-reports'] });
      toast.success('Report updated.');
    },
    onError: () => toast.error('Could not update that report.'),
  });

  const { openSupport } = useSupportChat();
  const advisorName = report.advisor_name || 'Advisor no longer on the platform';
  const reporterName = report.reporter_organization_name || report.reporter_organization_id;

  return (
    <Card
      className={report.is_urgent && report.status === 'open' ? 'border-red-300' : ''}
      data-testid={`advisor-report-${report.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          {report.is_urgent && (
            <Badge className="bg-red-600 hover:bg-red-600" data-testid="report-urgent">
              <AlertTriangle className="mr-1 h-3 w-3" />
              Urgent
            </Badge>
          )}
          <Badge
            className={STATUS_STYLE[report.status] ?? ''}
            variant="secondary"
            data-testid="report-status"
          >
            {report.status_display}
          </Badge>
          <Badge variant="outline">{report.category_display}</Badge>
          {report.unread > 0 && (
            <Badge className="bg-blue-600 hover:bg-blue-600" data-testid="report-unread">
              {report.unread} new
            </Badge>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {when(report.created_at)}
          </span>
        </div>

        <div className="flex items-center gap-3 pt-1">
          <PersonAvatar
            name={advisorName}
            photoUrl={report.advisor_photo_url}
            className="h-11 w-11"
            testId="report-advisor-photo"
          />
          <div className="min-w-0">
            <CardTitle className="text-base" data-testid="report-advisor-name">
              {report.advisor_profile_id ? (
                <a
                  href={advisorProfileUrl(report.advisor_profile_id)}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-1 hover:underline"
                  title={`See ${advisorName}'s profile`}
                  data-testid="report-advisor-profile-link"
                >
                  {advisorName}
                  <ExternalLink className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-70" />
                </a>
              ) : (
                advisorName
              )}
            </CardTitle>
            <CardDescription className="flex items-center gap-2">
              <PersonAvatar
                name={reporterName}
                photoUrl={report.reporter_avatar_url}
                className="h-5 w-5"
                testId="report-reporter-photo"
              />
              Reported by {reporterName}
              {report.reporter_email ? ` · ${report.reporter_email}` : ''}
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p
          className="whitespace-pre-wrap rounded-md bg-muted px-3 py-2 text-sm"
          data-testid="report-details"
        >
          {report.details}
        </p>

        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => openSupport(report.reporter_organization_id, reporterName)}
              data-testid="report-talk-customer"
            >
              <MessageSquare className="mr-1 h-3.5 w-3.5" />
              Talk to the customer
            </Button>
            {report.advisor_organization_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => openSupport(report.advisor_organization_id as string, advisorName)}
                data-testid="report-talk-advisor"
              >
                <MessageSquare className="mr-1 h-3.5 w-3.5" />
                Talk to the advisor
              </Button>
            )}
            <Button
              size="sm"
              variant={report.unread > 0 ? 'secondary' : 'ghost'}
              onClick={() => setTalking((open) => !open)}
              data-testid="report-toggle-thread"
            >
              {talking ? 'Hide the report thread' : 'Messages on this report'}
              {report.message_count > 0 && ` (${report.message_count})`}
            </Button>
          </div>
          {talking && <ReportThread reportId={report.id} />}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`notes-${report.id}`}>What we did about it</Label>
          <Textarea
            id={`notes-${report.id}`}
            rows={2}
            value={notes}
            onChange={(event) => {
              setNotes(event.target.value);
              setNotesTouched(true);
            }}
            placeholder="Who you spoke to, what was agreed, anything the next person needs."
            data-testid="report-notes"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!notesTouched || update.isPending}
              onClick={() => update.mutate({ admin_notes: notes })}
              data-testid="report-save-notes"
            >
              <Save className="mr-1 h-3.5 w-3.5" />
              Save notes
            </Button>

            <span className="mx-1 text-xs text-muted-foreground">Move to</span>
            {STATUSES.filter((s) => s.value !== report.status).map((s) => (
              <Button
                key={s.value}
                size="sm"
                variant="secondary"
                disabled={update.isPending}
                onClick={() => update.mutate({ status: s.value })}
                data-testid={`report-set-${s.value}`}
              >
                {s.label}
              </Button>
            ))}
          </div>
          {report.handled_by_email && (
            <p className="text-xs text-muted-foreground">
              Last touched by {report.handled_by_email} on {when(report.handled_at)}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdvisorReportsPage() {
  const [filter, setFilter] = React.useState('');

  const board = useQuery({
    queryKey: ['advisor-reports', filter],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/advisor-reports/', {
        params: filter ? { status: filter } : undefined,
      });
      return res.data as Board;
    },
  });

  const counts = board.data?.counts;
  const reports = board.data?.reports ?? [];

  return (
    <div className="space-y-6 p-6" data-testid="advisor-reports-page">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ShieldAlert className="h-6 w-6" />
          Advisor reports
        </h1>
        <p className="text-sm text-muted-foreground">
          What customers have told us about their advisors. The advisor is never
          shown any of this.
        </p>
      </div>

      {counts && (
        <div className="flex flex-wrap gap-3" data-testid="report-counts">
          <Badge variant="secondary" className="px-3 py-1">
            {counts.open} open
          </Badge>
          <Badge variant="secondary" className="px-3 py-1">
            {counts.investigating} being looked into
          </Badge>
          <Badge
            className={`px-3 py-1 ${counts.urgent_open ? 'bg-red-600 hover:bg-red-600' : ''}`}
            variant={counts.urgent_open ? 'default' : 'secondary'}
            data-testid="report-urgent-count"
          >
            {counts.urgent_open} urgent unresolved
          </Badge>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((option) => (
          <Button
            key={option.value || 'all'}
            size="sm"
            variant={filter === option.value ? 'default' : 'outline'}
            onClick={() => setFilter(option.value)}
            data-testid={`report-filter-${option.value || 'all'}`}
          >
            {option.label}
          </Button>
        ))}
      </div>

      {board.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : board.isError ? (
        <p className="py-16 text-center text-sm text-red-600" data-testid="reports-error">
          Could not load the board.
        </p>
      ) : reports.length === 0 ? (
        <p className="py-16 text-center text-sm text-muted-foreground" data-testid="reports-empty">
          {filter ? 'Nothing with that status.' : 'Nobody has reported an advisor.'}
        </p>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <ReportCard key={report.id} report={report} />
          ))}
        </div>
      )}
    </div>
  );
}
